import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { admin } from '../../services/api';
import type { UsageHistoryItem, UsageHistoryTotals, UsageDetailResponse, Pagination } from '../../types/api';
import {
  USAGE_TYPE_FILTER_OPTIONS,
  formatUsageFunctionalityLabel,
  getUsageFunctionalityBadgeStyle,
  resolveUsageFunctionalityKey,
} from '../../utils/usageFunctionality';
import DatePicker from '../ui/DatePicker';
import './usage/UsageBilling.css';
import { ChartCard, DailyBars, RankedBars } from './usage/UsageCharts';
import { dailySeries, rank } from './usage/usageChartData';
import { formatOptimizationDatasetDisplay } from '../../utils/optimizationDatasetName';
import { getDefaultTrainingDateRangeYmd } from '../../utils/defaultTrainingDateRange';
import {
  formatUsageTimelineCompleted,
  formatUsageTimelineCreated,
  mergeUsageDetailWithListItem,
  resolveUsageDurationMs,
  formatUsageDurationMs,
} from '../../utils/usageDateTime';

const PAGE_SIZE = 20;

const DEFAULT_USAGE_FILTER_DATES = getDefaultTrainingDateRangeYmd();

const AdminUsageHistoryTab: React.FC<{ onOpenAdminTour?: () => void; refreshToken?: number }> = ({ refreshToken = 0 }) => {
  const [items, setItems] = useState<UsageHistoryItem[]>([]);
  const [totals, setTotals] = useState<UsageHistoryTotals | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Draft filter values (what user sees in inputs)
  const [functionality, setFunctionality] = useState('');
  const [userId, setUserId] = useState('');
  const [dateFrom, setDateFrom] = useState(DEFAULT_USAGE_FILTER_DATES.from);
  const [dateTo, setDateTo] = useState(DEFAULT_USAGE_FILTER_DATES.to);

  // Applied filter values (what we actually query with; updated on "Apply filters")
  const [appliedFunctionality, setAppliedFunctionality] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState(DEFAULT_USAGE_FILTER_DATES.from);
  const [appliedDateTo, setAppliedDateTo] = useState(DEFAULT_USAGE_FILTER_DATES.to);
  const [excludeServiceUsage, setExcludeServiceUsage] = useState(false);

  const [detailItem, setDetailItem] = useState<UsageHistoryItem | null>(null);
  const [detail, setDetail] = useState<UsageDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const from = appliedDateFrom;
      const to = appliedDateTo;
      if (from && to && from > to) {
        setError('Invalid date range: From must be before or equal to To.');
        setLoading(false);
        return;
      }
      const params: Record<string, any> = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (appliedFunctionality) params.functionality = appliedFunctionality;
      if (appliedUserId.trim()) params.user_id = appliedUserId.trim();
      if (from) params.from = new Date(from).toISOString();
      if (to) {
        const toDate = new Date(to);
        if (from && from === to) {
          toDate.setUTCHours(23, 59, 59, 999);
          params.to = toDate.toISOString();
        } else {
          params.to = toDate.toISOString();
        }
      }
      if (excludeServiceUsage) {
        params.exclude_service_usage = true;
      }

      const resp = await admin.getUsageHistory(params);
      if (resp.success && resp.data) {
        setItems(resp.data.items || []);
        setTotals(resp.data.totals || null);
        setPagination(resp.data.pagination || null);
        setCurrentPage(page);
      } else {
        setError('Failed to load usage history');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load';
      const msgStr = typeof msg === 'string' ? msg : 'Failed to load';
      const isDateRangeError = /from.*before.*to|date must be/i.test(msgStr);
      const isInvalidUserId = /invalid\s*(user\s*)?id|user\s*not\s*found|no\s*(such\s*)?user|invalid\s*user_id/i.test(msgStr.toLowerCase()) ||
        (msgStr.toLowerCase().includes('user') && (msgStr.toLowerCase().includes('invalid') || msgStr.toLowerCase().includes('not found')));
      setError(
        isDateRangeError ? 'Invalid date range: From must be before or equal to To.' :
        isInvalidUserId ? 'The user ID you entered is incorrect. Please check the value and try again.' :
        msgStr
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFunctionality, appliedUserId, appliedDateFrom, appliedDateTo, excludeServiceUsage]);

  useEffect(() => { void load(1); }, [load]);

  // Кнопка обновления живёт в шапке раздела и дёргает активный срез.
  // Зависеть здесь от `load`/`currentPage` нельзя: эффект начал бы срабатывать
  // на каждую смену фильтров, а не только на явное нажатие.
  useEffect(() => {
    if (!refreshToken) return;
    void load(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const openDetail = async (item: UsageHistoryItem) => {
    if (!item.entity_id) return;
    setDetailItem(item);
    setDetail(null);
    setDetailLoading(true);
    try {
      const resp = await admin.getUsageDetail(
        resolveUsageFunctionalityKey(item.functionality, item.entity_id),
        item.entity_id,
        item.user_id ? { user_id: item.user_id } : undefined
      );
      setDetail(
        resp?.data ? mergeUsageDetailWithListItem(resp.data, item) : null
      );
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetailItem(null); setDetail(null); };
  const totalPages = pagination ? Math.ceil(pagination.total / PAGE_SIZE) : 1;
  const hasFilters = !!(appliedFunctionality || appliedUserId || appliedDateFrom || appliedDateTo);

  const handleApplyFilters = () => {
    setAppliedFunctionality(functionality);
    setAppliedUserId(userId);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const handleClearFilters = () => {
    const defaults = getDefaultTrainingDateRangeYmd();
    setFunctionality('');
    setUserId('');
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setAppliedFunctionality('');
    setAppliedUserId('');
    setAppliedDateFrom(defaults.from);
    setAppliedDateTo(defaults.to);
  };

  const daily = useMemo(() => dailySeries(items), [items]);
  const byFunctionality = useMemo(
    () =>
      rank(
        items,
        (item) => resolveUsageFunctionalityKey(item.functionality, item.entity_id),
        (item) => formatUsageFunctionalityLabel(item.functionality)
      ),
    [items]
  );

  const handleDateFromChange = (nextFrom: string) => {
    setDateFrom(nextFrom);
    if (nextFrom && dateTo && nextFrom > dateTo) setDateTo(nextFrom);
  };
  const handleDateToChange = (nextTo: string) => {
    setDateTo(nextTo);
    if (nextTo && dateFrom && nextTo < dateFrom) setDateFrom(nextTo);
  };

  return (
    <div className="admin-section admin-section--billing">

      {/* Totals */}
      {totals && (
        <div className="au-totals-grid">
          <div className="au-total-card au-total-card--primary">
            <div className="au-total-label">Total Tokens</div>
            <div className="au-total-value">{fmtNum(totals.total_tokens)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Prompt</div>
            <div className="au-total-value">{fmtNum(totals.prompt_tokens)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Completion</div>
            <div className="au-total-value">{fmtNum(totals.completion_tokens)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Cached</div>
            <div className="au-total-value">{fmtNum(totals.cached_tokens)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Entities</div>
            <div className="au-total-value">{fmtNum(totals.entities_count)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Records</div>
            <div className="au-total-value">{fmtNum(totals.records_count)}</div>
          </div>
          <div className="au-total-card">
            <div className="au-total-label">Total Cost</div>
            <div className="au-total-value">{fmtCost(totals.total_cost)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="au-filters" id="admin-billing-filters">
        <div className={`au-filter-field ${functionality ? 'au-filter-field--active' : ''}`}>
          <label>Type</label>
          <select value={functionality} onChange={e => setFunctionality(e.target.value)}>
            {USAGE_TYPE_FILTER_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className={`au-filter-field ${userId.trim() ? 'au-filter-field--active' : ''}`}>
          <label>User ID</label>
          <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="UUID" />
        </div>
        <div className={`au-filter-field ${dateFrom ? 'au-filter-field--active' : ''}`}>
          <DatePicker label="From" value={dateFrom} onChange={handleDateFromChange} placeholder="From date" max={dateTo || undefined} />
        </div>
        <div className={`au-filter-field ${dateTo ? 'au-filter-field--active' : ''}`}>
          <DatePicker label="To" value={dateTo} onChange={handleDateToChange} placeholder="To date" min={dateFrom || undefined} />
        </div>
        <div className={`au-filter-field au-filter-field--checkbox${excludeServiceUsage ? ' au-filter-field--active' : ''}`}>
          <label
            className="au-filter-checkbox"
            title="Hides usage produced by optimization actors and AI dataset generation."
          >
            <input
              type="checkbox"
              checked={excludeServiceUsage}
              onChange={(e) => {
                setExcludeServiceUsage(e.target.checked);
                setCurrentPage(1);
              }}
            />
            <span>Exclude service usage</span>
          </label>
        </div>
        <div className="au-apply-wrap">
          <span className="au-apply-label" aria-hidden="true">Apply</span>
          <button type="button" className="au-apply-btn" onClick={() => handleApplyFilters()} disabled={loading}>
            <i className="fas fa-filter"></i> Apply filters
          </button>
        </div>
        {hasFilters && (
          <div className="au-clear-wrap">
            <span className="au-clear-label" aria-hidden="true">Clear</span>
            <button type="button" className="au-clear-btn" onClick={() => handleClearFilters()}>
              <i className="fas fa-times"></i> Clear
            </button>
          </div>
        )}
      </div>

      {/* Active filters indicator */}
      {hasFilters && (
        <div className="au-active-filters">
          <span className="au-active-filters-label">Active filters:</span>
          <div className="au-active-filters-chips">
            {appliedFunctionality && (
              <span className="au-filter-chip">
                Type: {USAGE_TYPE_FILTER_OPTIONS.find(f => f.value === appliedFunctionality)?.label || appliedFunctionality}
                <button type="button" className="au-filter-chip-remove" onClick={() => { setFunctionality(''); setAppliedFunctionality(''); }} aria-label="Remove Type filter"><i className="fas fa-times"></i></button>
              </span>
            )}
            {appliedUserId.trim() && (
              <span className="au-filter-chip">
                User ID: {appliedUserId.length > 20 ? appliedUserId.slice(0, 16) + '…' : appliedUserId}
                <button type="button" className="au-filter-chip-remove" onClick={() => { setUserId(''); setAppliedUserId(''); }} aria-label="Remove User ID filter"><i className="fas fa-times"></i></button>
              </span>
            )}
            {appliedDateFrom && (
              <span className="au-filter-chip">
                From: {appliedDateFrom}
                <button type="button" className="au-filter-chip-remove" onClick={() => { setDateFrom(''); setAppliedDateFrom(''); }} aria-label="Remove From filter"><i className="fas fa-times"></i></button>
              </span>
            )}
            {appliedDateTo && (
              <span className="au-filter-chip">
                To: {appliedDateTo}
                <button type="button" className="au-filter-chip-remove" onClick={() => { setDateTo(''); setAppliedDateTo(''); }} aria-label="Remove To filter"><i className="fas fa-times"></i></button>
              </span>
            )}
          </div>
        </div>
      )}

      {error && <div className="admin-message error" style={{ marginBottom: 16 }}>{error}</div>}

      {items.length > 0 && (
        <div className="ub-charts">
          <ChartCard title="Spend by day" hint="records on this page">
            <DailyBars points={daily} emptyText="No dated records on this page." />
          </ChartCard>
          <ChartCard title="By functionality" hint="tokens on this page">
            <RankedBars slices={byFunctionality} emptyText="Nothing to break down yet." />
          </ChartCard>
        </div>
      )}


      {loading && items.length === 0 ? (
        <div className="admin-loading"><div className="spinner"></div><p>Loading...</p></div>
      ) : items.length === 0 ? (
        <div className="ub-table-wrap">
          <div className="ub-empty">
            <i className="fas fa-receipt" aria-hidden="true" />
            <div className="ub-empty__title">No usage in this range</div>
            <div className="ub-empty__hint">
              Shadow rows written by ML for reconciliation are excluded from this report by design, so a quiet period
              really does look empty here. Widen the dates, clear the type filter or drop the user ID.
            </div>
          </div>
        </div>
      ) : (
        <>
          {pagination && (
            <div className="au-count">Showing {items.length} of {fmtNum(pagination.total)} records</div>
          )}

          {/* Desktop table */}
          <div className="au-table-wrap" id="admin-billing-table-wrap">
            <table className="au-table">
              <colgroup>
                <col className="au-col-date" />
                <col className="au-col-email" />
                <col className="au-col-type" />
                <col className="au-col-status" />
                <col className="au-col-num" />
                <col className="au-col-num" />
                <col className="au-col-num" />
                <col className="au-col-num" />
                <col className="au-col-num" />
                <col className="au-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th title="Date & time">Date</th>
                  <th title="User email">Email</th>
                  <th title="Operation type">Type</th>
                  <th title="Status">Status</th>
                  <th className="au-th-num" title="Prompt tokens">Prompt</th>
                  <th className="au-th-num" title="Completion tokens">Compl.</th>
                  <th className="au-th-num" title="Total tokens">Total</th>
                  <th className="au-th-num" title="Cached tokens">Cached</th>
                  <th className="au-th-num" title="Total cost">Cost</th>
                  <th className="au-th-action" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const clickable = !!item.entity_id;
                  return (
                    <tr
                      key={`${item.entity_id || idx}-${item.created_at}`}
                      className={clickable ? 'au-row-clickable' : ''}
                      onClick={() => clickable && openDetail(item)}
                    >
                      <td className="au-td-date">
                        {formatUsageTimelineCreated(item)}
                      </td>
                      <td className="au-td-email" title={item.email || undefined}>
                        {item.email}
                      </td>
                      <td className="au-td-type">
                        <FuncBadge value={resolveUsageFunctionalityKey(item.functionality, item.entity_id)} />
                      </td>
                      <td className="au-td-status">
                        <StatusDot value={item.status} />
                      </td>
                      <td className="au-td-num">{fmtNum(item.prompt_tokens)}</td>
                      <td className="au-td-num">{fmtNum(item.completion_tokens)}</td>
                      <td className="au-td-num au-td-num--strong">{fmtNum(item.total_tokens)}</td>
                      <td className="au-td-num">{fmtNum(item.cached_tokens)}</td>
                      <td className="au-td-num">{fmtCost(item.total_cost)}</td>
                      <td className="au-td-action">
                        {clickable && <i className="fas fa-chevron-right au-row-arrow"></i>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="au-cards">
            {items.map((item, idx) => {
              const clickable = !!item.entity_id;
              return (
                <div
                  key={`m-${item.entity_id || idx}-${item.created_at}`}
                  className={`au-card ${clickable ? 'au-card--clickable' : ''}`}
                  onClick={() => clickable && openDetail(item)}
                >
                  <div className="au-card-top">
                    <FuncBadge value={resolveUsageFunctionalityKey(item.functionality, item.entity_id)} />
                    <StatusDot value={item.status} />
                  </div>
                  <div className="au-card-email">{item.email}</div>
                  <div className="au-card-date">{formatUsageTimelineCreated(item)}</div>
                  <div className="au-card-tokens">
                    <div className="au-card-token">
                      <span className="au-card-token-label">Total</span>
                      <span className="au-card-token-value au-card-token-value--bold">{fmtNum(item.total_tokens)}</span>
                    </div>
                    <div className="au-card-token">
                      <span className="au-card-token-label">Prompt</span>
                      <span className="au-card-token-value">{fmtNum(item.prompt_tokens)}</span>
                    </div>
                    <div className="au-card-token">
                      <span className="au-card-token-label">Compl.</span>
                      <span className="au-card-token-value">{fmtNum(item.completion_tokens)}</span>
                    </div>
                    {item.cached_tokens > 0 && (
                      <div className="au-card-token">
                        <span className="au-card-token-label">Cached</span>
                        <span className="au-card-token-value">{fmtNum(item.cached_tokens)}</span>
                      </div>
                    )}
                    <div className="au-card-token">
                      <span className="au-card-token-label">Cost</span>
                      <span className="au-card-token-value">{fmtCost(item.total_cost)}</span>
                    </div>
                  </div>
                  {clickable && (
                    <div className="au-card-hint">
                      Tap to view details <i className="fas fa-arrow-right" style={{ fontSize: 10 }}></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="au-pagination">
              <button className="au-page-btn" disabled={currentPage <= 1 || loading} onClick={() => load(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              <span className="au-page-info">Page {currentPage} / {totalPages}</span>
              <button className="au-page-btn" disabled={currentPage >= totalPages || loading} onClick={() => load(currentPage + 1)}>
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {detailItem && (
        <div className="au-modal-overlay">
          <div className="au-modal">
            <div className="au-modal-header">
              <h3>
                <FuncBadge value={resolveUsageFunctionalityKey(detailItem.functionality, detailItem.entity_id)} />
                <span style={{ marginLeft: 8 }}>Usage Detail</span>
              </h3>
              <button onClick={closeDetail} className="au-modal-close"><i className="fas fa-times"></i></button>
            </div>

            {detailLoading ? (
              <div className="au-modal-loading">Loading...</div>
            ) : !detail ? (
              <div className="au-modal-loading" style={{ color: '#7F9AB3' }}>Could not load details.</div>
            ) : (
              <>
                <div className="au-detail-grid">
                  <InfoField label="User">{detail.email || detail.user_id || '—'}</InfoField>
                  <InfoField label="Status"><StatusDot value={detail.status} /></InfoField>
                  <InfoField label="Created">
                    {formatUsageTimelineCreated(detail, detailItem)}
                  </InfoField>
                  <InfoField label="Completed">
                    {formatUsageTimelineCompleted(detail, detailItem)}
                  </InfoField>
                  {(() => {
                    const durationMs = resolveUsageDurationMs(detail, detailItem);
                    return durationMs != null ? (
                      <InfoField label="Duration">{formatUsageDurationMs(durationMs)}</InfoField>
                    ) : null;
                  })()}
                  {detail.entity_id && <InfoField label="Entity ID"><code className="au-code">{detail.entity_id}</code></InfoField>}
                  {detail.evaluation_run_id && (
                    <InfoField label="Evaluation Run">
                      <code className="au-code">{detail.evaluation_run_id}</code>
                    </InfoField>
                  )}
                  {detail.optimization_run_id && <InfoField label="Run ID"><code className="au-code">{detail.optimization_run_id}</code></InfoField>}
                  {detail.functionality === 'optimization' &&
                    formatOptimizationDatasetDisplay(detail.dataset_name) && (
                      <InfoField label="Dataset">
                        {formatOptimizationDatasetDisplay(detail.dataset_name)}
                      </InfoField>
                    )}
                  {detail.functionality === 'optimization' && detail.iterations != null && <InfoField label="Iterations">{detail.iterations}</InfoField>}
                  {detail.training_job_id && <InfoField label="Training Job"><code className="au-code">{detail.training_job_id}</code></InfoField>}
                  {detail.experiment_id && <InfoField label="Experiment"><code className="au-code">{detail.experiment_id}</code></InfoField>}
                  {detail.chat_id && <InfoField label="Chat"><code className="au-code">{detail.chat_id}</code></InfoField>}
                </div>

                <div className="au-totals-grid" style={{ marginBottom: 16 }}>
                  <div className="au-total-card au-total-card--primary"><div className="au-total-label">Total</div><div className="au-total-value">{fmtNum(detail.usage.total_tokens)}</div></div>
                  <div className="au-total-card"><div className="au-total-label">Prompt</div><div className="au-total-value">{fmtNum(detail.usage.prompt_tokens)}</div></div>
                  <div className="au-total-card"><div className="au-total-label">Completion</div><div className="au-total-value">{fmtNum(detail.usage.completion_tokens)}</div></div>
                  <div className="au-total-card"><div className="au-total-label">Cached</div><div className="au-total-value">{fmtNum(detail.usage.cached_tokens)}</div></div>
                  <div className="au-total-card"><div className="au-total-label">Records</div><div className="au-total-value">{fmtNum(detail.usage.records_count)}</div></div>
                  <div className="au-total-card"><div className="au-total-label">Total Cost</div><div className="au-total-value">{fmtCost(detail.usage.total_cost)}</div></div>
                </div>

                {detail.usage.by_operation && detail.usage.by_operation.length > 0 && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 600, color: '#4A6683', marginBottom: 8 }}>Breakdown by Operation</h4>
                    <div className="au-table-wrap">
                      <table className="au-table au-table--compact">
                        <colgroup>
                          <col className="au-col-op" />
                          <col className="au-col-num" />
                          <col className="au-col-num" />
                          <col className="au-col-num" />
                          <col className="au-col-num" />
                          <col className="au-col-num" />
                          <col className="au-col-num" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th title="Operation">Operation</th>
                            <th className="au-th-num" title="Prompt tokens">Prompt</th>
                            <th className="au-th-num" title="Completion tokens">Compl.</th>
                            <th className="au-th-num" title="Total tokens">Total</th>
                            <th className="au-th-num" title="Cached tokens">Cached</th>
                            <th className="au-th-num" title="Total cost">Cost</th>
                            <th className="au-th-num" title="Record count">Rec.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.usage.by_operation.map((op, i) => (
                            <tr key={i}>
                              <td className="au-td-op" title={op.operation}>
                                {op.operation}
                              </td>
                              <td className="au-td-num">{fmtNum(op.prompt_tokens)}</td>
                              <td className="au-td-num">{fmtNum(op.completion_tokens)}</td>
                              <td className="au-td-num au-td-num--strong">{fmtNum(op.total_tokens)}</td>
                              <td className="au-td-num">{fmtNum(op.cached_tokens)}</td>
                              <td className="au-td-num">{fmtCost(op.total_cost)}</td>
                              <td className="au-td-num">{fmtNum(op.records_count)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile operation cards */}
                    <div className="au-cards">
                      {detail.usage.by_operation.map((op, i) => (
                        <div key={i} className="au-card" style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{op.operation}</div>
                          <div className="au-card-tokens">
                            <div className="au-card-token"><span className="au-card-token-label">Total</span><span className="au-card-token-value au-card-token-value--bold">{fmtNum(op.total_tokens)}</span></div>
                            <div className="au-card-token"><span className="au-card-token-label">Prompt</span><span className="au-card-token-value">{fmtNum(op.prompt_tokens)}</span></div>
                            <div className="au-card-token"><span className="au-card-token-label">Compl.</span><span className="au-card-token-value">{fmtNum(op.completion_tokens)}</span></div>
                            <div className="au-card-token"><span className="au-card-token-label">Cost</span><span className="au-card-token-value">{fmtCost(op.total_cost)}</span></div>
                            <div className="au-card-token"><span className="au-card-token-label">Records</span><span className="au-card-token-value">{fmtNum(op.records_count)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Helpers ---
const fmtNum = (v: number | null | undefined) => (v ?? 0).toLocaleString();
const fmtCost = (v: number | null | undefined) => {
  const value = v ?? 0;
  return `$${value < 1 && value > 0 ? value.toFixed(4) : value.toFixed(2)}`;
};

const FuncBadge: React.FC<{ value: string }> = ({ value }) => {
  const c = getUsageFunctionalityBadgeStyle(value);
  const label = formatUsageFunctionalityLabel(value);
  return (
    <span className="au-func-badge" style={{ background: c.bg, color: c.color }}>
      <i className={`fas ${c.icon}`} style={{ fontSize: 9 }}></i> {label}
    </span>
  );
};

/** Align with Fine Tuning Job Details: succeeded uses success green (#0072C6), not warning orange. */
const statusColors: Record<string, string> = {
  completed: '#0072C6',
  succeeded: '#0072C6',
  running: '#FF8300',
  pending: '#FF8300',
  failed: '#FF8300',
};
const StatusDot: React.FC<{ value: string }> = ({ value }) => {
  const color = statusColors[value?.toLowerCase()] || '#5B7893';
  return <span className="au-status-badge" style={{ color }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }}></span>{value}</span>;
};

const InfoField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="au-detail-field">
    <div className="au-detail-field-label">{label}</div>
    <div>{children}</div>
  </div>
);

export default AdminUsageHistoryTab;
